// components/common/footer/Footer.jsx
import React, { useState, useEffect } from "react";
import {
  FiMapPin,
  FiPhone,
  FiMail,
  FiFacebook,
  FiInstagram,
  FiYoutube,
  FiLinkedin,
  FiTwitter,
  FiSmartphone,
  FiTablet,
  FiWatch,
} from "react-icons/fi";
import {
  FaCcMastercard,
  FaCcVisa,
  FaCcPaypal,
  FaCcDiscover,
  FaCcAmex,
} from "react-icons/fa";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  HOME_ROUTE,
  ABOUT_ROUTE,
  CONTACT_ROUTE,
  FAQ_ROUTE,
  PRIVACY_ROUTE,
  TERMS_ROUTE,
  CATEGORY_ROUTE,
  BRAND_ROUTE,
} from "../../../routes/constant";

const Footer = ({ isDarkMode }) => {
  const { categories } = useSelector((state) => state.home);

  const [footerData, setFooterData] = useState({
    contact: {
      address: "Bhapra Road, Samalkha, Haryana-132101",
      phone: "7404252555",
      email: "admin@tap2cash.in",
      companyName: "Selltech E-Waste Management Solution Pvt Ltd.",
      cin: "U62099HR2024PTC123824",
      supportNumber: "7404252555",
      grievanceOfficer: {
        name: "Abhishek Punia",
        email: "care@tap2cash.in",
      },
    },
    socialLinks: [
      {
        id: 1,
        name: "Facebook",
        url: "https://www.facebook.com/profile.php?id=61561102217942",
        icon: "FiFacebook",
      },
      {
        id: 2,
        name: "Instagram",
        url: "https://www.instagram.com/tap2cash/?hl=en",
        icon: "FiInstagram",
      },
      {
        id: 3,
        name: "YouTube",
        url: "https://www.youtube.com/@Tap2Cash",
        icon: "FiYoutube",
      },
      {
        id: 4,
        name: "LinkedIn",
        url: "https://www.linkedin.com/company/tap2cash",
        icon: "FiLinkedin",
      },
      {
        id: 5,
        name: "Twitter",
        url: "https://twitter.com/tap2cash",
        icon: "FiTwitter",
      },
    ],
    categories: [],
    brands: [],
    infoLinks: [
      { id: 1, name: "Contact", route: CONTACT_ROUTE },
      { id: 2, name: "Privacy", route: PRIVACY_ROUTE },
      { id: 3, name: "Terms", route: TERMS_ROUTE },
      { id: 4, name: "About", route: ABOUT_ROUTE },
      { id: 5, name: "FAQs", route: FAQ_ROUTE },
    ],
    paymentMethods: [
      { id: 1, name: "Mastercard", icon: "FaCcMastercard" },
      { id: 2, name: "Visa", icon: "FaCcVisa" },
      { id: 3, name: "PayPal", icon: "FaCcPaypal" },
      { id: 4, name: "Discover", icon: "FaCcDiscover" },
      { id: 5, name: "American Express", icon: "FaCcAmex" },
    ],
    copyrightYear: new Date().getFullYear(),
    trademarkNotice:
      "** All trademarks, logos, product and brand names belong to their respective owners.",
  });

  useEffect(() => {
    if (categories && categories.length > 0) {
      // Get categories for display
      const displayCategories = categories.map((cat) => ({
        id: cat.id,
        name: cat.name,
        slug: cat.slug,
        display_name: cat.displayName || cat.display_name || cat.name,
        icon: getCategoryIconName(cat.name),
      }));

      // Get brands from the FIRST category that has brands
      let brandList = [];
      for (const category of categories) {
        if (category.brands && category.brands.length > 0) {
          brandList = category.brands.slice(0, 6).map((brand) => ({
            id: brand.id,
            name: brand.name,
            slug: brand.slug,
            categorySlug: category.slug,
          }));
          break; // Stop after first category with brands
        }
      }

      setFooterData((prev) => ({
        ...prev,
        categories: displayCategories,
        brands: brandList,
      }));
    }
  }, [categories]);

  const getCategoryIconName = (categoryName) => {
    switch (categoryName?.toLowerCase()) {
      case "smart phone":
      case "smartphone":
        return "FiSmartphone";
      case "tablet":
        return "FiTablet";
      case "smart watch":
      case "smartwatch":
        return "FiWatch";
      default:
        return "FiSmartphone";
    }
  };

  const getIcon = (iconName, className = "w-3.5 h-3.5") => {
    const icons = {
      FiFacebook: <FiFacebook className={className} />,
      FiInstagram: <FiInstagram className={className} />,
      FiYoutube: <FiYoutube className={className} />,
      FiLinkedin: <FiLinkedin className={className} />,
      FiTwitter: <FiTwitter className={className} />,
      FiSmartphone: <FiSmartphone className={className} />,
      FiTablet: <FiTablet className={className} />,
      FiWatch: <FiWatch className={className} />,
      FaCcMastercard: <FaCcMastercard className={className} />,
      FaCcVisa: <FaCcVisa className={className} />,
      FaCcPaypal: <FaCcPaypal className={className} />,
      FaCcDiscover: <FaCcDiscover className={className} />,
      FaCcAmex: <FaCcAmex className={className} />,
    };
    return icons[iconName] || null;
  };

  return (
    <footer
      className={`border-t ${isDarkMode ? "border-gray-800 bg-gray-900" : "border-gray-200 bg-white"}`}
    >
      <div className="px-4 py-8 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <div className="grid grid-cols-2 gap-6 mb-6 md:grid-cols-4">
          {/* Contact Section */}
          <div className="col-span-2 md:col-span-1">
            <h3
              className={`text-[12px] font-bold uppercase mb-3 ${isDarkMode ? "text-gray-200" : "text-gray-800"}`}
            >
              Contact
            </h3>
            <div
              className={`space-y-2 text-[12px] font-medium ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
            >
              <div className="flex items-start gap-2">
                <FiMapPin
                  className={`${isDarkMode ? "text-green-400" : "text-green-600"} mt-0.5 flex-shrink-0`}
                  size={16}
                />
                <span className="font-medium">
                  {footerData.contact.address}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <FiPhone
                  className={`${isDarkMode ? "text-green-400" : "text-green-600"} flex-shrink-0`}
                  size={16}
                />
                <span className="font-medium">{footerData.contact.phone}</span>
              </div>
              <div className="flex items-center gap-2">
                <FiMail
                  className={`${isDarkMode ? "text-green-400" : "text-green-600"} flex-shrink-0`}
                  size={16}
                />
                <span className="font-medium">{footerData.contact.email}</span>
              </div>
            </div>

            <div className="flex items-center gap-2 mt-4">
              {footerData.socialLinks.map((social) => (
                <a
                  key={social.id}
                  href={social.url}
                  rel="me"
                  target="_blank"
                  className={`w-8 h-8 rounded-full flex items-center justify-center transition-all ${
                    isDarkMode
                      ? "bg-gray-800 text-gray-300 hover:bg-green-600 hover:text-white"
                      : "bg-gray-200 text-gray-700 hover:bg-green-600 hover:text-white"
                  }`}
                >
                  {getIcon(social.icon, "w-4 h-4")}
                </a>
              ))}
            </div>
          </div>

          {/* Category Section - From homeSlice categories */}
          <div>
            <h3
              className={`text-[12px] font-bold uppercase mb-3 ${isDarkMode ? "text-gray-200" : "text-gray-800"}`}
            >
              Category
            </h3>
            <ul className="space-y-2">
              {footerData.categories.map((category) => (
                <li key={category.id}>
                  <Link
                    to={CATEGORY_ROUTE(category.slug)}
                    className={`text-[12px] font-medium flex items-center gap-2 transition-colors ${
                      isDarkMode
                        ? "text-gray-300 hover:text-green-400"
                        : "text-gray-700 hover:text-green-600"
                    }`}
                  >
                    {getIcon(category.icon, "w-3.5 h-3.5")}
                    {category.display_name || category.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Brand Section - From first category's brands */}
          <div>
            <h3
              className={`text-[12px] font-bold uppercase mb-3 ${isDarkMode ? "text-gray-200" : "text-gray-800"}`}
            >
              Brand
            </h3>
            <ul className="space-y-2">
              {footerData.brands.map((brand) => (
                <li key={brand.id}>
                  <Link
                    to={BRAND_ROUTE(brand.categorySlug, brand.slug)}
                    className={`text-[12px] font-medium transition-colors ${
                      isDarkMode
                        ? "text-gray-300 hover:text-green-400"
                        : "text-gray-700 hover:text-green-600"
                    }`}
                  >
                    {brand.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Information Section */}
          <div>
            <h3
              className={`text-[12px] font-bold uppercase mb-3 ${isDarkMode ? "text-gray-200" : "text-gray-800"}`}
            >
              Info
            </h3>
            <ul className="space-y-2">
              {footerData.infoLinks.map((link) => (
                <li key={link.id}>
                  <Link
                    to={link.route}
                    className={`text-[12px] font-medium transition-colors ${
                      isDarkMode
                        ? "text-gray-300 hover:text-green-400"
                        : "text-gray-700 hover:text-green-600"
                    }`}
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Registered Office */}
        <div
          className={`border-t ${isDarkMode ? "border-gray-800" : "border-gray-200"} pt-4 mt-4`}
        >
          <h3
            className={`text-[12px] font-bold mb-2 ${isDarkMode ? "text-gray-200" : "text-gray-800"}`}
          >
            Registered Office:
          </h3>
          <div
            className={`space-y-1 text-[12px] font-medium ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
          >
            <p>
              {footerData.contact.companyName} | {footerData.contact.address}
            </p>
            <p>
              Support: {footerData.contact.supportNumber} | CIN:{" "}
              {footerData.contact.cin}
            </p>
            <p>
              Grievance Officer: {footerData.contact.grievanceOfficer.name} |{" "}
              <a
                href={`mailto:${footerData.contact.grievanceOfficer.email}`}
                className={`${isDarkMode ? "text-green-400" : "text-green-600"} font-bold hover:underline`}
              >
                {footerData.contact.grievanceOfficer.email}
              </a>
            </p>
            <p
              className={`text-[12px] font-medium ${isDarkMode ? "text-gray-400" : "text-gray-600"} mt-2`}
            >
              {footerData.trademarkNotice}
            </p>
          </div>
        </div>

        {/* Copyright and Payment */}
        <div
          className={`border-t ${isDarkMode ? "border-gray-800" : "border-gray-200"} pt-4 mt-4 flex flex-col md:flex-row justify-between items-center gap-3`}
        >
          <p
            className={`text-[12px] font-bold ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
          >
            © {footerData.copyrightYear}{" "}
            <Link
              to={HOME_ROUTE}
              className={`${isDarkMode ? "text-green-400" : "text-green-600"} font-bold hover:underline`}
            >
              Tap2Cash
            </Link>{" "}
            All rights reserved
          </p>

          <div className="flex items-center gap-2">
            {footerData.paymentMethods.map((method) => (
              <span key={method.id} className="text-2xl">
                {getIcon(method.icon, "w-6 h-6")}
              </span>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;